import type { ActionBarCrossSellCardModel } from '~/utils/shop/buildActionBarCrossSellCards';
import type { BvsSimulationProductSelection } from '~/stores/productDetailPageStore/bvsSimulationSelectionStore';

/** Synthetic group ids for products the recommendations do not cover. */
export const BVS_SIMULATION_GROUP_IDS = {
  lens: 'bvs-simulation-lens',
  light: 'bvs-simulation-light'
} as const;

/** Analytics labels for the synthetic cards; not shown in the UI. */
const BVS_SIMULATION_GROUP_LABELS = {
  lens: 'BVS simulation lens',
  light: 'BVS simulation light'
} as const;

/**
 * Puts the lens and light of the BVS simulation in front of the action bar
 * cross-selling and marks them as highlighted. A product that the
 * recommendations already offer is promoted instead of duplicated, so no
 * product is listed twice. Products that are already in the cart stack are
 * left out entirely.
 */
export function applyBvsSimulationCrossSellCards(params: {
  cards: ActionBarCrossSellCardModel[];
  lens: BvsSimulationProductSelection | null;
  light: BvsSimulationProductSelection | null;
  stackSkus: ReadonlyArray<string>;
}): ActionBarCrossSellCardModel[] {
  const selections = [
    { selection: params.lens, type: 'lens' as const },
    { selection: params.light, type: 'light' as const }
  ];

  const remainingCards = [...params.cards];
  const highlightedCards: ActionBarCrossSellCardModel[] = [];

  for (const { selection, type } of selections) {
    if (!selection || params.stackSkus.includes(selection.sku)) {
      continue;
    }

    if (highlightedCards.some((card) => card.productSku === selection.sku)) {
      continue;
    }

    const recommendedIndex = remainingCards.findIndex((card) =>
      card.variants.some(
        (variant: { sku: string }) => variant.sku === selection.sku
      )
    );

    if (recommendedIndex >= 0) {
      const [recommendedCard] = remainingCards.splice(recommendedIndex, 1);
      highlightedCards.push({
        ...recommendedCard,
        productSku: selection.sku,
        selectedVariantSku: selection.sku,
        highlighted: true
      });
      continue;
    }

    highlightedCards.push(createSimulationCard(selection, type));
  }

  return [...highlightedCards, ...remainingCards];
}

function createSimulationCard(
  selection: BvsSimulationProductSelection,
  type: 'lens' | 'light'
): ActionBarCrossSellCardModel {
  return {
    groupId: BVS_SIMULATION_GROUP_IDS[type],
    groupLabel: BVS_SIMULATION_GROUP_LABELS[type],
    productSku: selection.sku,
    displayName: selection.name,
    selectedVariantSku: selection.sku,
    variants: [
      {
        sku: selection.sku,
        name: selection.name,
        lengthLabel: '',
        priceValue: selection.priceValue,
        currency: selection.currency,
        shippingTimeDays: null,
        imageJson: null
      }
    ],
    priceDeltaText: selection.priceText,
    imageJson: null,
    imageUrl: selection.imageUrl,
    highlighted: true
  };
}
